"use client";

import { THEME_COOKIE } from "@/utils/constants";
import { isPlayRoute } from "@/utils/url";
import { usePathname } from "next/navigation";
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

type ThemeContextType = {
  switchTheme: (theme: string) => void;
  theme?: string;
};

export const ThemeContext = createContext<ThemeContextType>({
  theme: undefined,
  switchTheme: () => null,
});

type ThemeContextProps = {
  initialTheme?: string;
};

export const ThemeContextProvider = ({
  children,
  initialTheme,
}: PropsWithChildren<ThemeContextProps>) => {
  const [theme, setTheme] = useState(initialTheme);

  const pathName = usePathname();

  const isPlay = isPlayRoute(pathName);
  const effectiveTheme = isPlay ? "dark" : theme;

  useEffect(() => {
    if (effectiveTheme) {
      document.documentElement.setAttribute("data-mode", effectiveTheme);
    } else {
      document.documentElement.removeAttribute("data-mode");
    }
  }, [effectiveTheme]);

  const switchTheme = (updatedTheme: string) => {
    if (Boolean(updatedTheme)) {
      // Setting cookie this way instead of in a server action to prevent refreshing server components
      document.cookie = `theme=${updatedTheme}; expires=${new Date(
        Date.now() + 365 * 24 * 60 * 60 * 1000
      )}; path=/`;
      setTheme(updatedTheme);

      if (!isPlay) {
        document.documentElement.setAttribute("data-mode", updatedTheme);
      }
    } else {
      document.cookie = `${THEME_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      setTheme(undefined);

      if (!isPlay) {
        document.documentElement.removeAttribute("data-mode");
      }
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        switchTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  return useContext(ThemeContext);
};
